/**
 * Open Dental API — SuperAdmin copy and client-side rules (Documents/OD-API-PLANNING.md §1.13 b, Phase 0.7).
 *
 * ONE home for the tier wording: the tier dropdown, the legend under the toggle and the node-test all
 * read these constants, so the descriptions can never disagree with each other.
 *
 * The server (main-service od-api-config.js) is the authority on every rule — Super Admin only, OD_API
 * on only with a tier AND a stored key, the key never returned. The checks here only save a round trip
 * and explain why a control is disabled; they never replace the server's.
 *
 * Import-free: cypress/node-tests/od-api-superadmin.test.js executes this file verbatim.
 */

// What LIVE Teamio writes go through the API at each tier (plan §1.4 as amended, §1.18 DF5-1, §1.19 F4)
// and what each needs beyond the office-wide requirements below. Keep in step with the senders: an
// item appears here only once its release has shipped. `requires` null = nothing beyond the note.
export const OD_API_TIER_DESCRIPTIONS = [
  {
    tier: 1,
    label: 'Tier 1 — Comm Log + documents ($15/office/mo)',
    summary: 'These writes go through the Open Dental API; everything else stays on the current sync path.',
    items: [
      { what: 'Comm Log notes (messages, email confirmations, task comments, follow-up notes)', requires: null },
      { what: 'Documents and images (texted photos, Photo to OD, form attachments)', requires: 'Teamio app 3.0.60+' },
    ],
    later: null,
  },
  {
    tier: 2,
    label: 'Tier 2 — Everything except Payments ($30/office/mo)',
    summary: 'Everything in Tier 1, plus appointment updates. Payments always stay on the current sync path.',
    items: [
      { what: 'Everything in Tier 1', requires: null },
      { what: 'Appointment confirmations (text / email replies, first-message status)', requires: null },
      { what: 'ASAP availability notes on appointments', requires: null },
      { what: 'Practice Pulse seated / dismissed times', requires: 'Open Dental 25.2.10+ (older versions ignore these times)' },
      { what: 'Insurance verification dates (patient + plan) and the appointment field', requires: null },
      { what: 'Claim tracking status (Copy to Claim History)', requires: null },
      { what: 'Treatment-plan signatures (saved plans; active plans stay on the sync path)', requires: null },
    ],
    later: 'Coming to Tier 2 in later releases: forms and online booking.',
  },
];

export const OD_API_REQUIREMENTS_NOTE =
  'Requires a tier and a customer API key. The office must be on Open Dental v22.2.16+ with the OD API Service running on the server. Not available yet for customers with clinics. Turning this on moves only the writes listed under the selected tier; anything else — and any write the API refuses — goes through the current sync path automatically.';

// The per-site exe secret (plan §1.15): how the office's Teamio app proves which office it is when it
// fetches the Open Dental credential. Shown ONCE after Issue/Reissue; only its hash is stored. It goes
// in the app's setup screen ("Teamio Exe Secret", teamio-exe index.html), which saves it to
// C:\ProgramData\Teamio; settings.json is only the fallback for an app without that field.
export const OD_API_EXE_SECRET_HELP =
  'On the office server, open the Teamio app\'s setup screen, paste this into the "Teamio Exe Secret" field and Submit. It will not be shown again. Reissuing revokes the previous secret at once. (An older Teamio app without that field: add it to settings.json as "odApiExeSecret" and restart the app.)';

export const OD_API_REISSUE_CONFIRM =
  'Reissue the exe secret for this office? The current one stops working immediately: the office app falls back to the current sync path until the new secret is entered in the Teamio app\'s setup screen ("Teamio Exe Secret").';

/** Same cap as the server (main-service lib/od-customer-key.js MAX_CUSTOMER_KEY_BYTES). */
export const OD_API_MAX_KEY_BYTES = 159;

/** null when the pasted key is sendable, else why not. Never echoes the key. */
export function customerKeyProblem(key) {
  if (typeof key !== 'string' || key === '') return 'Paste the customer key first.';
  if (/\s/.test(key)) return 'The key contains spaces or line breaks — paste just the key.';
  if (key.indexOf('/') !== -1) return "The key contains '/', which an Open Dental customer key never does.";
  if (new TextEncoder().encode(key).length > OD_API_MAX_KEY_BYTES) return 'The key is longer than any Open Dental customer key.';
  return null;
}

/** Why the switch can't be turned ON right now, or null. Turning OFF is never blocked. */
export function turnOnBlockedReason(config) {
  if (!config) return 'Loading…';
  if (config.OD_API === 1) return null;
  if (!config.OpenDentalAPITier && !config.keySet) return 'Pick a tier and save a customer key first.';
  if (!config.OpenDentalAPITier) return 'Pick a tier first.';
  if (!config.keySet) return 'Save a customer key first.';
  return null;
}

/** The server's error text for a failed request, or a generic fallback. */
export function describeOdApiError(err, fallback) {
  const data = err && err.response && err.response.data;
  const msg = data && typeof data.error === 'string' ? data.error : null;
  return msg || fallback;
}
